// src/components/SEO/index.tsx
import React from 'react';
import { Helmet } from 'react-helmet'; // You'll need to install this package
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  article?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = 'to:text - Next generation transcription',
  description = 'Transform audio and video to perfect text with AI-powered transcription. No subscriptions, pay as you go, and GDPR compliant.',
  image = '/img/og-image.jpg',
  article = false,
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  
  // Create canonical URLs for all language versions
  const baseUrl = 'https://totext.eu';
  const currentUrl = `${baseUrl}${pathname}`;
  
  // Create language-specific canonical URLs
  const getLanguageUrl = (langCode: string) => {
    const pathSegments = pathname.split('/').filter(Boolean);
    const currentLangInUrl = i18n.languages.includes(pathSegments[0]) ? pathSegments[0] : null;
    
    if (currentLangInUrl) {
      // Replace language in path
      return `${baseUrl}${pathname.replace(`/${currentLangInUrl}`, `/${langCode}`)}`;
    } else {
      // Add language to path
      return `${baseUrl}/${langCode}${pathname}`;
    }
  };

  // Create language links for all supported languages
  const languageLinks = i18n.languages.map(langCode => ({
    lang: langCode,
    url: getLanguageUrl(langCode),
  }));

  return (
    <Helmet>
      {/* Basic metadata */}
      <html lang={i18n.language} />
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Canonical link */}
      <link rel="canonical" href={currentUrl} />

      {/* Language alternates for SEO */}
      {languageLinks.map(({ lang, url }) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={url}
        />
      ))}
      
      {/* Default hreflang */}
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/en${pathname}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:locale" content={i18n.language} />
      <meta property="og:site_name" content="to:text" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
    </Helmet>
  );
};

export default SEO;